import { FunctionComponent } from 'react';

import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import isEmpty from 'lodash/isEmpty';

import { useAccountGet } from '@sbv-clients/data-access-api';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import { useMobileDrawerStore } from '@sbv-clients/shared-state';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  useTheme,
  Icon,
} from '@sbv-clients/ui-shared';

import BreadCrumbs from './bread-crumb';
import { DBSwitcher } from './db-switcher';
import {
  ImpersonatedLabel,
  ImpersonatedLabelMobileView,
} from './impersonated-label';
import { SupportFacilitySwitcher } from './support-facility-switcher';
import { NavbarProps } from './typings/navbar-props';
import { UserDropdown } from './user-dropdown';

export const Navbar: FunctionComponent<NavbarProps> = ({
  onToggleDrawer,
  toggleSidebarMinimized,
  isSidebarMinimized,
  ssoUserName,
  minimisedViewAllowed,
  breadCrumbLabels,
  showPreferences,
  showProfiles,
  showResetConfiguration,
  showMobileEmployeesButton,
  profileImage,
  showRemoteSupport,
  showContactUs,
  extraMenuItems,
}) => {
  const { impersonatorId } = useAuth();
  const { data: accountData } = useAccountGet();
  const isSupportSession = accountData?.isSupportSession ?? false;
  const theme = useTheme();
  const { setMobileDrawerOpen } = useMobileDrawerStore();
  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        borderBottom: `1px solid ${theme.border?.profileTitle}`,
        backgroundColor: 'white',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          padding: { xs: '0 12px', md: '0 24px' },
          height: '60px',
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          {!isEmpty(impersonatorId) && <ImpersonatedLabel />}
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          color="black"
          width="100%"
          minHeight="60px"
          gap={1}
        >
          <Box
            display="flex"
            alignItems="center"
            minWidth={0}
            overflow="hidden"
            flex={1}
          >
            <IconButton
              sx={{
                display: { xs: 'block', md: 'none' },
                lineHeight: 0.5,
                mr: 2,
                flexShrink: 0,
              }}
              size="medium"
              color="inherit"
              aria-label="Open drawer"
              onClick={onToggleDrawer}
            >
              <Icon name={'Menu'} />
            </IconButton>
            {minimisedViewAllowed && isSidebarMinimized && (
              <IconButton
                sx={{
                  display: { xs: 'none', md: 'block' },
                  lineHeight: 0.5,
                  mr: 2,
                  flexShrink: 0,
                }}
                size="medium"
                color="inherit"
                onClick={toggleSidebarMinimized}
              >
                <Icon
                  style={{ transform: 'rotate(180deg)' }}
                  name={'CloseSideBarSVG'}
                />
              </IconButton>
            )}
            {showMobileEmployeesButton && (
              <Button
                onClick={() => setMobileDrawerOpen(true)}
                startIcon={
                  <PeopleAltOutlinedIcon sx={{ color: 'primary.main' }} />
                }
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  flexShrink: 0,
                  width: '120px',
                  height: '36px',
                  color: 'primary.main',
                  backgroundColor: 'primary.light',
                  fontWeight: 600,
                  borderRadius: '8px',
                  marginRight: '10px',
                }}
              >
                Employees
              </Button>
            )}
            <Box minWidth={0} overflow="hidden" width="100%">
              <BreadCrumbs breadCrumbLabels={breadCrumbLabels} />
            </Box>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            flexShrink={0}
            columnGap={{ xs: 1, md: 3 }}
          >
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {!isEmpty(impersonatorId) && <ImpersonatedLabelMobileView />}
            </Box>
            {isSupportSession ? (
              <SupportFacilitySwitcher />
            ) : (
              showProfiles && (
                <DBSwitcher
                  isRoot={breadCrumbLabels && breadCrumbLabels?.length < 1}
                />
              )
            )}
            <UserDropdown
              showRemoteSupport={showRemoteSupport}
              showContactUs={!isSupportSession && showContactUs}
              showPreferences={!isSupportSession && showPreferences}
              ssoUserName={ssoUserName}
              showResetConfiguration={
                !isSupportSession && showResetConfiguration
              }
              profileImage={profileImage}
              extraMenuItems={extraMenuItems}
            />
          </Box>
        </Box>
      </Box>
    </AppBar>
  );
};
